import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type Proveedor, type ProveedorRequest, type ProveedorFiltered } from '../types/proveedor';
import { ProveedorService } from '../services';

interface ProveedorState {
  // State
  proveedores: Proveedor[];
  filteredProveedores: ProveedorFiltered[];
  loading: boolean;
  error: string | null;
  selectedProveedor: Proveedor | null;

  // UI State
  dialogOpen: boolean;

  // Actions
  fetchProveedores: () => Promise<void>;
  fetchFilteredProveedores: () => Promise<void>;
  createProveedor: (proveedorRequest: ProveedorRequest) => Promise<void>;
  updateProveedor: (proveedorRequest: ProveedorRequest) => Promise<void>;
  saveProveedor: (proveedorRequest: ProveedorRequest) => Promise<void>;
  deleteProveedor: (idPersona: number) => Promise<void>;

  // UI Actions
  setSelectedProveedor: (proveedor: Proveedor | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;

  // Reset
  reset: () => void;
}

const initialState = {
  proveedores: [],
  filteredProveedores: [],
  loading: false,
  error: null,
  selectedProveedor: null,
  dialogOpen: false,
};

export const useProveedorStore = create<ProveedorState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchProveedores: async () => {
        set({ loading: true, error: null }, false, 'proveedores/fetchProveedores/start');

        try {
          const proveedores = await ProveedorService.getProveedores();
          set(
            { proveedores, loading: false },
            false,
            'proveedores/fetchProveedores/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch proveedores',
            },
            false,
            'proveedores/fetchProveedores/error'
          );
        }
      },

      fetchFilteredProveedores: async () => {
        set({ loading: true, error: null }, false, 'proveedores/fetchFilteredProveedores/start');

        try {
          const filteredProveedores = await ProveedorService.getFilteredProveedores();
          set(
            { filteredProveedores, loading: false },
            false,
            'proveedores/fetchFilteredProveedores/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch filtered proveedores',
            },
            false,
            'proveedores/fetchFilteredProveedores/error'
          );
        }
      },

      createProveedor: async (proveedorRequest: ProveedorRequest) => {
        set({ loading: true, error: null }, false, 'proveedores/createProveedor/start');

        try {
          const newProveedor = await ProveedorService.saveProveedor(proveedorRequest);
          set(
            (state) => ({
              proveedores: [...state.proveedores, newProveedor],
              loading: false,
              dialogOpen: false,
              selectedProveedor: null,
            }),
            false,
            'proveedores/createProveedor/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to create proveedor',
            },
            false,
            'proveedores/createProveedor/error'
          );
          throw error;
        }
      },

      updateProveedor: async (proveedorRequest: ProveedorRequest) => {
        set({ loading: true, error: null }, false, 'proveedores/updateProveedor/start');

        try {
          const updatedProveedor = await ProveedorService.saveProveedor(proveedorRequest);
          set(
            (state) => ({
              proveedores: state.proveedores.map((proveedor) =>
                proveedor.idPersona === updatedProveedor.idPersona ? updatedProveedor : proveedor
              ),
              loading: false,
              dialogOpen: false,
              selectedProveedor: null,
            }),
            false,
            'proveedores/updateProveedor/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to update proveedor',
            },
            false,
            'proveedores/updateProveedor/error'
          );
          throw error;
        }
      },

      saveProveedor: async (proveedorRequest: ProveedorRequest) => {
        const isUpdate = !!proveedorRequest.idPersona;
        if (isUpdate) {
          await get().updateProveedor(proveedorRequest);
        } else {
          await get().createProveedor(proveedorRequest);
        }
      },

      deleteProveedor: async (idPersona: number) => {
        set({ loading: true, error: null }, false, 'proveedores/deleteProveedor/start');

        try {
          // Note: Implement delete endpoint when available
          // await ProveedorService.deleteProveedor(idPersona);
          set(
            (state) => ({
              proveedores: state.proveedores.filter((proveedor) => proveedor.idPersona !== idPersona),
              loading: false,
            }),
            false,
            'proveedores/deleteProveedor/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to delete proveedor',
            },
            false,
            'proveedores/deleteProveedor/error'
          );
        }
      },

      // UI Actions
      setSelectedProveedor: (proveedor: Proveedor | null) => {
        set({ selectedProveedor: proveedor }, false, 'proveedores/setSelectedProveedor');
      },

      openDialog: () => {
        set({ dialogOpen: true }, false, 'proveedores/openDialog');
      },

      closeDialog: () => {
        set({ dialogOpen: false, selectedProveedor: null }, false, 'proveedores/closeDialog');
      },

      clearError: () => {
        set({ error: null }, false, 'proveedores/clearError');
      },

      // Reset
      reset: () => {
        set(initialState, false, 'proveedores/reset');
      },
    }),
    {
      name: 'proveedor-store',
    }
  )
);